import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { validateUsername } from "../../utils/validationUtils";
import { useRole } from "../../context/RoleContext";
import { fetchCustomersByEmail } from "../../api/customerAPI";
import { fetchUsersByEmail } from "../../api/userAPI";
import { checkUser } from "../../api/loginAPI";
import {
  loginWithBackend,
  forceChangePasswordWithBackend,
} from "../../api/loginAPI";
import useIsMobile from "../../common/mobileVersion/useIsMobile";
import Loader from "../../common/Loader";
import { getOrganizationId, setOrganizationId } from "../../utils/orgIdUtils";
import { routes } from "../../routes/routes";
import MaltoraLogo from "../landing/MaltoraLogo";
import { normalizeRole } from "../../utils/roleUtils";

export interface LoginScreenProps {
  onUserTypeChange?: (
    userType: "maltora-customer" | "maltora-seller" | "maltora-sales-rep"
  ) => void;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ onUserTypeChange }) => {
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [selectedUserType, setSelectedUserType] = useState<
    "maltora-customer" | "maltora-seller" | "maltora-sales-rep"
  >("maltora-customer");
  const [showPwd, setShowPwd] = useState(false);

  const [emailError, setEmailError] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [isForceChange, setIsForceChange] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const isMobile = useIsMobile();
  const navigate = useNavigate();

  useEffect(() => {
    if (onUserTypeChange) onUserTypeChange(selectedUserType);
  }, [selectedUserType, onUserTypeChange]);

  const { setRole } = useRole();

  const handleUserTypeChange = (
    userType: "maltora-customer" | "maltora-seller" | "maltora-sales-rep"
  ) => {
    setSelectedUserType(userType);
    setUsername("");
    setPassword("");
    setEmailError("");
    setPasswordError("");
    navigate(`/${routes.public.signIn}?type=${userType}`);
  };

  const handleLogin = async () => {
    setEmailError("");
    setPasswordError("");
    setLoading(true);

    const orgId = getOrganizationId();

    const usernameValidationError = validateUsername(username);
    const passwordMissing = password.trim() === "";

    if (usernameValidationError) {
      setEmailError(usernameValidationError);
      setLoading(false);
      return;
    }

    try {
      const response = await checkUser({ email: username });
      const emailExists = response.exists;

      if (!emailExists) {
        setEmailError("Email ID is not registered.");
        setLoading(false);
        return;
      }
    } catch (error) {
      setEmailError("Unable to verify email at the moment. Please try again.");
      console.error("checkUser validation error:", error);
      setLoading(false);
      return;
    }

    if (passwordMissing) {
      setPasswordError("Please enter your password.");
      setLoading(false);
      return;
    }

    try {
      if (isForceChange) {
        const session = localStorage.getItem("forceChangeSession") || "";
        if (!session) {
          setEmailError("Session missing for force change password.");
          setLoading(false);
          return;
        }

        const response = await forceChangePasswordWithBackend(
          username,
          password,
          session
        );

        const isSuccess =
          (response?.message &&
            response.message.toLowerCase().includes("success")) ||
          (response?.data?.message &&
            response.data.message.toLowerCase().includes("success"));

        if (isSuccess) {
          localStorage.removeItem("forceChangeSession");
          setIsForceChange(false);
          setPassword("");
          setEmailError("");
          setPasswordError("");
          setLoading(false);
          navigate(`/${routes.public.signIn}`);
        } else {
          setEmailError(
            response?.data?.message ||
              response?.message ||
              "Password update failed."
          );
          setLoading(false);
        }

        return;
      }

      const result = await loginWithBackend({
        emailId: username,
        password,
      });

      if (
        result?.data?.challengeName === "NEW_PASSWORD_REQUIRED" &&
        result?.data?.session
      ) {
        localStorage.setItem("forceChangeSession", result.data.session);
        setIsForceChange(true);
        setPassword("");
        setPasswordError("");
        setLoading(false);
        return;
      }

      if (!result?.data?.tokens?.accessToken) {
        setEmailError(result?.message || "Login failed.");
        setLoading(false);
        return;
      }

      localStorage.setItem("accessToken", result.data.tokens.accessToken);
      localStorage.setItem("idToken", result.data.tokens.idToken);
      const userType = normalizeRole(result.data.type);
      localStorage.setItem("userRole", userType);
      localStorage.setItem("userEmail", username);

      sessionStorage.setItem("refreshToken", result.data.tokens.refreshToken);

      if (result.data.organizationId) {
        setOrganizationId(result.data.organizationId);
      }

      // Store trial data if available
      if (result.data.trial?.daysRemaining !== undefined) {
        console.log(
          "Storing trialDaysRemaining:",
          result.data.trial.daysRemaining
        );
        localStorage.setItem(
          "trialDaysRemaining",
          result.data.trial.daysRemaining.toString()
        );
      } else {
        console.log("No trial data found in response:", result.data);
      }

      console.log("Backend returned role:", result.data.type);
      console.log("Final userType after mapping:", userType);
      setRole(userType);
      const userEmail = username;

      if (userType === "maltora-customer") {
        try {
          const response = await fetchCustomersByEmail({
            emailId: userEmail,
          });

          const customerName = response?.customer?.outletName || "";
          const customerId = response?.customer?.id || "";

          localStorage.setItem("Name", customerName);
          localStorage.setItem("customerId", customerId);
        } catch (e) {
          localStorage.setItem("Name", "");
          localStorage.setItem("customerId", "");
        }

        setLoading(false);
        setTimeout(() => navigate(`/maltora-customer/home`), 100);
      } else if (userType === "maltora-seller") {
        try {
          const response = await fetchUsersByEmail({
            emailId: userEmail,
          });

          const sellerName = response?.data?.data?.seller?.name || "";

          localStorage.setItem("Name", sellerName);
        } catch (e) {
          localStorage.setItem("Name", "");
        }

        setLoading(false);

        // Check if this is first login for seller
        if (result.data.isFirstLogin) {
          setTimeout(() => navigate(`/maltora-seller/first-sign-in`), 100);
        } else {
          setTimeout(() => navigate(`/maltora-seller/dashboard/sales`), 100);
        }
      } else if (userType === "maltora-sales-rep") {
        try {
          const response = await fetchUsersByEmail({
            emailId: userEmail,
          });

          const salesRepName = response?.data?.data?.seller?.name || "";

          localStorage.setItem("Name", salesRepName);
        } catch (e) {
          localStorage.setItem("Name", "");
        }

        setLoading(false);
        setTimeout(() => navigate(`/maltora-sales-rep/dashboard/sales`), 100);
      } else {
        console.error("Unrecognized role:", userType);
        console.error("Original role from backend:", result.data.type);
        setLoading(false);
        alert(`Role not recognized: ${userType}. Please contact support.`);
      }
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.error?.type ||
        error?.response?.data?.error?.message ||
        "";

      if (
        error.errCode === "NotAuthorizedException" ||
        errorMsg.toLowerCase().includes("incorrect username or password")
      ) {
        setPasswordError("Incorrect username or password.");
      } else if (error.errCode === "UserNotConfirmedException") {
        setEmailError("Email ID is not confirmed.");
      } else {
        console.error("Unhandled login error:", error);
        setEmailError("An unexpected error occurred. Please try again.");
      }

      setLoading(false);
    }
  };

  useEffect(() => {
    const orgId = getOrganizationId();

    localStorage.clear();

    if (orgId) {
      setOrganizationId(orgId);
    }
  }, []);

  const BENEFITS = [
    {
      title: "Digital Orders",
      desc: "Replace WhatsApp chaos with structured, trackable orders.",
      icon: (
        <>
          <path d="M9 11l3 3L22 4" />
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </>
      ),
    },
    {
      title: "Credit Control",
      desc: "Set limits, track outstanding, and automate reminders.",
      icon: (
        <>
          <rect x="1" y="4" width="22" height="16" rx="2" />
          <line x1="1" y1="10" x2="23" y2="10" />
        </>
      ),
    },
    {
      title: "Live Inventory",
      desc: "Know your stock levels across every warehouse in real time.",
      icon: (
        <>
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
        </>
      ),
    },
    {
      title: "Full Visibility",
      desc: "One dashboard for your entire supply chain, end-to-end.",
      icon: (
        <>
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </>
      ),
    },
  ];

  const inputStyle: React.CSSProperties = {
    width: "100%",
    height: "46px",
    border: "1.5px solid rgba(15,23,42,0.14)",
    borderRadius: "8px",
    padding: "0 14px 0 42px",
    fontFamily: "var(--font-ui, DM Sans, system-ui, sans-serif)",
    fontSize: "14px",
    color: "#0F172A",
    background: "#F8F9FA",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s",
  };

  return (
    <>
      <Loader show={loading} />
      <div
        style={{
          display: "flex",
          minHeight: "100vh",
          fontFamily: "var(--font-ui, DM Sans, system-ui, sans-serif)",
        }}
      >
        {/* LEFT */}
        <div
          style={{
            width: "420px",
            flexShrink: 0,
            background: "linear-gradient(160deg,#0F2D2B 0%,#1F5F5B 100%)",
            display: "flex",
            flexDirection: "column",
            padding: "48px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              bottom: "-80px",
              right: "-80px",
              width: "300px",
              height: "300px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.03)",
              pointerEvents: "none",
            }}
          />
          <Link
            to="/"
            aria-label="Maltora home"
            style={{
              display: "block",
              width: "110px",
              marginBottom: "60px",
              flexShrink: 0,
            }}
          >
            <MaltoraLogo fill="#fff" />
          </Link>
          <h2
            style={{
              fontFamily:
                "var(--font-display, Satoshi, DM Sans, system-ui, sans-serif)",
              fontSize: "26px",
              fontWeight: "700",
              color: "#fff",
              lineHeight: "1.3",
              letterSpacing: "-0.02em",
              marginBottom: "40px",
            }}
          >
            Everything your beverage
            <br />
            business needs,{" "}
            <em style={{ fontStyle: "italic", color: "rgba(255,255,255,0.6)" }}>
              in one place.
            </em>
          </h2>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            {BENEFITS.map((b) => (
              <div
                key={b.title}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "14px",
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "8px",
                    background: "rgba(255,255,255,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="rgba(255,255,255,0.85)"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {b.icon}
                  </svg>
                </div>
                <div>
                  <div
                    style={{
                      fontWeight: "700",
                      fontSize: "14px",
                      color: "#fff",
                      marginBottom: "2px",
                    }}
                  >
                    {b.title}
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "rgba(255,255,255,0.55)",
                      lineHeight: "1.5",
                    }}
                  >
                    {b.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT */}
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#fff",
            padding: "40px 24px",
          }}
        >
          <div style={{ width: "100%", maxWidth: "400px" }}>
            <h1
              style={{
                fontFamily:
                  "var(--font-display, Satoshi, DM Sans, system-ui, sans-serif)",
                fontSize: "28px",
                fontWeight: "700",
                color: "#0F172A",
                marginBottom: "6px",
                letterSpacing: "-0.02em",
              }}
            >
              Welcome back
            </h1>
            <p
              style={{
                fontSize: "14px",
                color: "#64748B",
                marginBottom: "32px",
              }}
            >
              Sign in to your Maltora account
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!loading) handleLogin();
              }}
              noValidate
            >
              <div style={{ marginBottom: "18px" }}>
                <label
                  htmlFor="email"
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#0F172A",
                    marginBottom: "6px",
                  }}
                >
                  Email
                </label>
                <div style={{ position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      left: "14px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#64748B",
                      display: "flex",
                    }}
                  >
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </span>
                  <input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    autoComplete="email"
                    aria-label="Email address"
                    style={{
                      ...inputStyle,
                      borderColor: emailError
                        ? "#ef4444"
                        : "rgba(15,23,42,0.14)",
                    }}
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setEmailError("");
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#1F5F5B")}
                    onBlur={(e) =>
                      (e.target.style.borderColor = emailError
                        ? "#ef4444"
                        : "rgba(15,23,42,0.14)")
                    }
                    required
                  />
                </div>
                {emailError && (
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#ef4444",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    {emailError}
                  </span>
                )}
              </div>
              <div style={{ marginBottom: "16px" }}>
                <label
                  htmlFor="password"
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#0F172A",
                    marginBottom: "6px",
                  }}
                >
                  Password
                </label>
                <div style={{ position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      left: "14px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#64748B",
                      display: "flex",
                    }}
                  >
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="11" width="18" height="11" rx="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                  <input
                    id="password"
                    type={showPwd ? "text" : "password"}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    aria-label="Password"
                    style={{
                      ...inputStyle,
                      paddingRight: "44px",
                      borderColor: passwordError
                        ? "#ef4444"
                        : "rgba(15,23,42,0.14)",
                    }}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordError("");
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#1F5F5B")}
                    onBlur={(e) =>
                      (e.target.style.borderColor = passwordError
                        ? "#ef4444"
                        : "rgba(15,23,42,0.14)")
                    }
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#64748B",
                      display: "flex",
                      padding: 0,
                    }}
                    aria-label="Toggle password visibility"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                </div>
                {passwordError && (
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#ef4444",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    {passwordError}
                  </span>
                )}
              </div>
              {!isForceChange && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "28px",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "13px",
                      color: "#64748B",
                      cursor: "pointer",
                    }}
                  >
                    {/* <input type="checkbox" style={{accentColor:'#1F5F5B',cursor:'pointer'}} /> Remember me */}
                  </label>
                  <Link
                    to={`/${routes.public.forgotPassword}`}
                    style={{
                      fontSize: "13px",
                      color: "#1F5F5B",
                      textDecoration: "none",
                      fontWeight: "600",
                    }}
                  >
                    Forgot password?
                  </Link>
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  height: "48px",
                  background: "#E86A0D",
                  color: "#fff",
                  fontFamily: "var(--font-ui, DM Sans, system-ui, sans-serif)",
                  fontSize: "15px",
                  fontWeight: "700",
                  border: "none",
                  borderRadius: "10px",
                  cursor: loading ? "not-allowed" : "pointer",
                  letterSpacing: "0.01em",
                  transition: "background 0.15s",
                  marginBottom: "20px",
                  opacity: loading ? 0.7 : 1,
                }}
                onMouseEnter={(e) =>
                  !loading && (e.currentTarget.style.background = "#d45e0b")
                }
                onMouseLeave={(e) =>
                  !loading && (e.currentTarget.style.background = "#E86A0D")
                }
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
              <p
                style={{
                  textAlign: "center",
                  fontSize: "13px",
                  color: "#64748B",
                  fontFamily: '"DM Sans", system-ui, sans-serif',
                }}
              >
                Don&apos;t have an account?{" "}
                <Link
                  to={`/${routes.public.signUp}`}
                  style={{
                    color: "#1F5F5B",
                    fontSize: "13px",
                    fontWeight: 600,
                    textDecoration: "none",
                    fontFamily: '"DM Sans", system-ui, sans-serif',
                  }}
                >
                  Get started free
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginScreen;
